import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'
import {
	FileText,
	Briefcase,
	Newspaper,
	Search,
	Layers,
} from 'lucide-react'
import { useState, useRef, useEffect, useCallback } from 'react'
import { Link } from 'react-router'
import { type SearchResult } from '#app/routes/resources.search.tsx'
import { useIsSafari } from '#app/utils/safari-detection.ts'

const GooeyFilter = () => {
	return (
		<svg
			aria-hidden="true"
			style={{ position: 'absolute', width: 0, height: 0 }}
		>
			<defs>
				<filter id="goo-effect">
					<feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
					<feColorMatrix
						in="blur"
						type="matrix"
						values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -15"
						result="goo"
					/>
					<feComposite in="SourceGraphic" in2="goo" operator="atop" />
				</filter>
			</defs>
		</svg>
	)
}

const SearchIcon = ({ isUnsupported }: { isUnsupported: boolean }) => {
	return (
		<motion.svg
			initial={{
				opacity: 0,
				scale: 0.8,
				x: -4,
				filter: isUnsupported ? 'none' : 'blur(5px)',
			}}
			animate={{
				opacity: 1,
				scale: 1,
				x: 0,
				filter: 'blur(0px)',
			}}
			exit={{
				opacity: 0,
				scale: 0.8,
				x: -4,
				filter: isUnsupported ? 'none' : 'blur(5px)',
			}}
			transition={{
				delay: 0.1,
				duration: 1,
				type: 'spring',
				bounce: 0.15,
			}}
			width="15"
			height="15"
			viewBox="0 0 15 15"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M10 6.5C10 8.433 8.433 10 6.5 10C4.567 10 3 8.433 3 6.5C3 4.567 4.567 3 6.5 3C8.433 3 10 4.567 10 6.5ZM9.30884 10.0159C8.53901 10.6318 7.56251 11 6.5 11C4.01472 11 2 8.98528 2 6.5C2 4.01472 4.01472 2 6.5 2C8.98528 2 11 4.01472 11 6.5C11 7.56251 10.6318 8.53901 10.0159 9.30884L12.8536 12.1464C13.0488 12.3417 13.0488 12.6583 12.8536 12.8536C12.6583 13.0488 12.3417 13.0488 12.1464 12.8536L9.30884 10.0159Z"
				fillRule="evenodd"
				clipRule="evenodd"
				fill="currentColor"
			/>
		</motion.svg>
	)
}

const LoadingIcon = () => {
	return (
		<svg
			className="gooey-loading-icon"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 256 256"
			aria-label="Loading"
			role="status"
		>
			<rect width="256" height="256" fill="none" />
			<line
				x1="128"
				y1="32"
				x2="128"
				y2="64"
				fill="none"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="16"
			/>
			<line
				x1="195.88"
				y1="60.12"
				x2="173.25"
				y2="82.75"
				fill="none"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="16"
				opacity="0.9"
			/>
			<line
				x1="224"
				y1="128"
				x2="192"
				y2="128"
				fill="none"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="16"
				opacity="0.8"
			/>
			<line
				x1="195.88"
				y1="195.88"
				x2="173.25"
				y2="173.25"
				fill="none"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="16"
				opacity="0.7"
			/>
			<line
				x1="128"
				y1="224"
				x2="128"
				y2="192"
				fill="none"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="16"
				opacity="0.6"
			/>
			<line
				x1="60.12"
				y1="195.88"
				x2="82.75"
				y2="173.25"
				fill="none"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="16"
				opacity="0.5"
			/>
			<line
				x1="32"
				y1="128"
				x2="64"
				y2="128"
				fill="none"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="16"
				opacity="0.4"
			/>
			<line
				x1="60.12"
				y1="60.12"
				x2="82.75"
				y2="82.75"
				fill="none"
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="16"
				opacity="0.3"
			/>
		</svg>
	)
}

const ResultIcon = ({
	type,
	index,
}: {
	type: SearchResult['type']
	index: number
}) => {
	const Icon =
		type === 'page'
			? FileText
			: type === 'case'
				? Briefcase
				: type === 'service'
					? Layers
					: Newspaper

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ delay: index * 0.12 + 0.3 }}
			className="gooey-info-icon"
		>
			<Icon className="h-4 w-4" />
		</motion.div>
	)
}

const buttonVariants = {
	initial: { x: 0, width: 100 },
	step1: { x: 0, width: 100 },
	step2: { x: 0, width: 180 },
}

const iconVariants = {
	hidden: { x: -50, opacity: 0 },
	visible: { x: 216, opacity: 1 },
}

const useDebounce = (value: string, delay: number) => {
	const [debouncedValue, setDebouncedValue] = useState(value)

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}


const getResultItemVariants = (index: number, isUnsupported: boolean) => ({
	initial: {
		y: 0,
		scale: 0.3,
		filter: isUnsupported ? 'none' : 'blur(10px)',
		opacity: 0,
	},
	animate: {
		y: (index + 1) * 50,
		scale: 1,
		filter: 'blur(0px)',
		opacity: 1,
	},
	exit: {
		y: 0,
		scale: 0.3,
		filter: isUnsupported ? 'none' : 'blur(10px)',
		opacity: 0,
	},
})

const getResultItemTransition = (index: number, _totalItems: number = 5) => ({
	duration: 0.75,
	delay: index * 0.12,
	type: 'spring' as const,
	bounce: 0.35,
})

export const GooeySearchBar = () => {
	const inputRef = useRef<HTMLInputElement>(null)
	const wrapperRef = useRef<HTMLDivElement>(null)
	const abortControllerRef = useRef<AbortController | null>(null)
	const lastSearchRef = useRef<string>('')

	const [state, setState] = useState({
		step: 1,
		searchText: '',
		showResults: false, // Track results visibility separately
	})
	const [searchResults, setSearchResults] = useState<SearchResult[]>([])
	const [isLoading, setIsLoading] = useState(false)

	const debouncedSearchText = useDebounce(state.searchText, 500)
	const isUnsupported = useIsSafari()

	const handleButtonClick = () => {
		setState((prevState) => ({ ...prevState, step: 2, showResults: true }))
	}

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setState((prevState) => ({ ...prevState, searchText: e.target.value }))
	}

	const closeSearch = useCallback(() => {
		// Cancel any pending search
		if (abortControllerRef.current) {
			abortControllerRef.current.abort()
			abortControllerRef.current = null
		}
		lastSearchRef.current = ''
		// First, hide results while keeping them mounted for exit animation
		setState((prevState) => ({ ...prevState, step: 1, showResults: false }))
		// Clear the text after all animations complete (longest exit animation is ~0.55s)
		setTimeout(() => {
			setState({ step: 1, searchText: '', showResults: false })
		}, 600)
	}, [])

	const handleResultClick = useCallback(() => {
		closeSearch()
	}, [closeSearch])

	const handleEscape = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				closeSearch()
			}
		},
		[closeSearch],
	)

	// Handle clicks outside the search bar
	const handleClickOutside = useCallback(
		(e: MouseEvent) => {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(e.target as Node)
			) {
				closeSearch()
			}
		},
		[closeSearch],
	)

	// Handle blur event on the input
	const handleInputBlur = useCallback(
		(_e: React.FocusEvent<HTMLInputElement>) => {
			// Small delay to allow clicking on results
			setTimeout(() => {
				// Check if the focus is still within the wrapper
				if (
					wrapperRef.current &&
					!wrapperRef.current.contains(document.activeElement)
				) {
					closeSearch()
				}
			}, 200)
		},
		[closeSearch],
	)

	useEffect(() => {
		if (state.step === 2) {
			inputRef.current?.focus()
			document.addEventListener('keydown', handleEscape)
			document.addEventListener('mousedown', handleClickOutside)
		} else {
			// Cancel any pending search when closing
			if (abortControllerRef.current) {
				abortControllerRef.current.abort()
				abortControllerRef.current = null
			}
			lastSearchRef.current = ''
			document.removeEventListener('keydown', handleEscape)
			document.removeEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('keydown', handleEscape)
			document.removeEventListener('mousedown', handleClickOutside)
			// Cleanup on unmount
			if (abortControllerRef.current) {
				abortControllerRef.current.abort()
			}
		}
	}, [state.step, handleEscape, handleClickOutside])

	useEffect(() => {
		// Only make a request if the search text has actually changed and meets requirements
		if (
			debouncedSearchText &&
			debouncedSearchText.length >= 2 &&
			debouncedSearchText !== lastSearchRef.current
		) {
			// Cancel previous request if it exists
			if (abortControllerRef.current) {
				abortControllerRef.current.abort()
			}

			// Create new abort controller for this request
			const controller = new AbortController()
			abortControllerRef.current = controller
			lastSearchRef.current = debouncedSearchText

			setIsLoading(true)
			fetch(
				`/resources/search?q=${encodeURIComponent(debouncedSearchText)}`,
				{ signal: controller.signal },
			)
				.then((res) => res.json() as Promise<{ results: SearchResult[] }>)
				.then((data) => {
					if (!controller.signal.aborted) {
						setSearchResults(data.results)
						setIsLoading(false)
					}
				})
				.catch((err) => {
					if (err instanceof Error && err.name !== 'AbortError') {
						setIsLoading(false)
					}
				})
		} else if (!debouncedSearchText && lastSearchRef.current) {
			// Clear search results when search text is cleared
			lastSearchRef.current = ''
			setSearchResults([])
			if (abortControllerRef.current) {
				abortControllerRef.current.abort()
				abortControllerRef.current = null
			}
		}
	}, [debouncedSearchText])

	return (
		<div
			ref={wrapperRef}
			className={clsx('gooey-wrapper', isUnsupported && 'no-goo')}
		>
			<GooeyFilter />

			<div className="gooey-button-content">
				<motion.div
					className="gooey-button-content-inner"
					initial="initial"
					animate={state.step === 1 ? 'step1' : 'step2'}
					transition={{ duration: 0.75, type: 'spring', bounce: 0.15 }}
				>
					<AnimatePresence mode="popLayout">
						{state.showResults && (
							<motion.div
								key="search-text-wrapper"
								className="gooey-search-results"
								role="listbox"
								aria-label="Search results"
								initial={{ scale: 0.8, opacity: 0 }}
								animate={{ scale: 1, opacity: 1 }}
								exit={{ scale: 0.8, opacity: 0 }}
								transition={{ duration: 0.5 }}
							>
								<AnimatePresence mode="popLayout">
									{state.showResults &&
										searchResults.map((item, index) => (
											<motion.div
												key={`${item.type}-${item.id}`}
												whileHover={{
													scale: 1.02,
													transition: { duration: 0.2 },
												}}
												variants={getResultItemVariants(index, isUnsupported)}
												initial="initial"
												animate="animate"
												exit="exit"
												transition={getResultItemTransition(
													index,
													searchResults.length,
												)}
												className="gooey-search-result"
												role="option"
											>
												<Link
													to={item.url}
													onClick={handleResultClick}
													className="gooey-search-result-link"
												>
													<div className="gooey-search-result-title">
														<ResultIcon type={item.type} index={index} />
														<motion.span
															initial={{ opacity: 0 }}
															animate={{ opacity: 1 }}
															transition={{ delay: index * 0.12 + 0.3 }}
														>
															{item.title}
															{item.subtitle && (
																<span className="gooey-search-result-subtitle">
																	{' - '}
																	{item.subtitle}
																</span>
															)}
														</motion.span>
													</div>
												</Link>
											</motion.div>
										))}
									{state.showResults &&
										state.searchText &&
										debouncedSearchText === state.searchText &&
										!isLoading &&
										searchResults.length === 0 && (
											<motion.div
												variants={getResultItemVariants(0, isUnsupported)}
												initial="initial"
												animate="animate"
												exit="exit"
												transition={getResultItemTransition(0, 1)}
												className="gooey-search-result"
												style={{ pointerEvents: 'none' }}
											>
												<div className="gooey-search-result-title">
													<Search className="gooey-info-icon h-4 w-4" />
													<span>Geen resultaten gevonden</span>
												</div>
											</motion.div>
										)}
								</AnimatePresence>
							</motion.div>
						)}
					</AnimatePresence>

					<motion.div
						variants={buttonVariants}
						onClick={handleButtonClick}
						whileHover={{ scale: state.step === 2 ? 1 : 1.05 }}
						whileTap={{ scale: 0.95 }}
						className="gooey-search-btn"
						role="button"
						aria-label={state.step === 1 ? 'Zoek' : undefined}
					>
						{state.step === 1 ? (
							<span className="gooey-search-text">Zoek</span>
						) : (
							<input
								ref={inputRef}
								type="text"
								className="gooey-search-input"
								placeholder="Type om te zoeken..."
								aria-label="Search input"
								onChange={handleSearch}
								onBlur={handleInputBlur}
								value={state.searchText}
							/>
						)}
					</motion.div>

					<AnimatePresence mode="wait">
						{state.step === 2 && (
							<motion.div
								key="icon"
								className="gooey-separate-element"
								initial="hidden"
								animate="visible"
								exit="hidden"
								variants={iconVariants}
								transition={{
									delay: 0.1,
									duration: 0.85,
									type: 'spring',
									bounce: 0.15,
								}}
							>
								{!isLoading ? (
									<SearchIcon isUnsupported={isUnsupported} />
								) : (
									<LoadingIcon />
								)}
							</motion.div>
						)}
					</AnimatePresence>
				</motion.div>
			</div>
		</div>
	)
}
